/* The phone's chrome: what the top bar cannot hold once the screen is four
 * inches wide.
 *
 * Two pieces, and they are apart for a reason.
 *
 * STRIPS sit above the board. Due now and Waiting are lists the desk reads
 * whole, side by side with the service menu, because there is room for that on a
 * monitor. On a phone they are two counts on one line — "Due now 3 · Waiting 2"
 * — and the lists themselves are a tap away. The count is the part that gets
 * glanced at ("is anybody about to walk in"); the rows are the part that gets
 * acted on, and acting is already a tap.
 *
 * BAR is fixed to the bottom, and that is the whole argument for it: the top of
 * a phone held one-handed is the part of the screen a thumb cannot reach. The
 * nav that lives in the console's top bar — Board, History, Settings, End day —
 * is four things pressed all day, so it comes down here, and the two pressed
 * once a day go behind the ⋯.
 *
 * ＋ is in the middle and drawn as the one primary thing, because starting a
 * turn is what somebody standing on the floor with a phone is doing. See
 * service-sheet.js for what it opens.
 */
import * as Vue from 'vue';
import { store } from '../store.js';
import { DueNow } from './due-now.js';
import { PendingMoves } from './pending-moves.js';
import { WaitingList } from './waiting-list.js';

/* ---------------- the two counts above the board ---------------- */

export const PhoneStrips = {
  name: 'PhoneStrips',
  components: { DueNow: DueNow, PendingMoves: PendingMoves, WaitingList: WaitingList },
  setup: function () {
    // Same licence gate as the desk bar: a salon whose plan lapsed may still
    // have rows sitting there, and a list they cannot be sold the other half of
    // is a support call rather than a feature.
    var waiting = Vue.computed(function () {
      return store.checkinLicensed.value ? store.waitingCheckins.value : [];
    });

    var due = Vue.computed(function () {
      return store.schedulingOn.value ? store.dueSoon.value : [];
    });

    // Anything late is the reason to open it rather than the count itself, so
    // it is marked on the closed strip.
    var lateDue = Vue.computed(function () {
      return due.value.filter(function (o) { return o.late; }).length;
    });

    function open(kind) {
      store.ui.phoneList = store.ui.phoneList === kind ? null : kind;
    }

    return {
      ui: store.ui,
      due: due,
      lateDue: lateDue,
      waiting: waiting,
      longest: store.longestWaitMinutes,
      open: open,
      closed: store.dayClosed
    };
  },
  template: [
    /* Its own row above the two counts, drawing nothing when nothing is open. */
    '<pending-moves />',
    /* Nothing due and nobody waiting is the ordinary state of a salon, and in
       it this whole band is not there — an empty strip saying "Due now 0" is a
       line of the screen spent on the absence of news. */
    '<div v-if="!closed && (due.length || waiting.length)" class="pstrips">',
    '  <div class="pstrips__row">',
    '    <button v-if="due.length" type="button" class="pstrip"',
    '      :class="{ \'is-open\': ui.phoneList === \'due\', \'is-late\': lateDue }"',
    '      :aria-expanded="ui.phoneList === \'due\' ? \'true\' : \'false\'"',
    '      @click="open(\'due\')">',
    '      Due now <span class="pstrip__n">{{ due.length }}</span>',
    '      <span v-if="lateDue" class="pstrip__late">{{ lateDue }} late</span>',
    '      <span class="pstrip__caret" aria-hidden="true">{{ ui.phoneList === "due" ? "\\u2039" : "\\u203A" }}</span>',
    '    </button>',
    '    <button v-if="waiting.length" type="button" class="pstrip"',
    '      :class="{ \'is-open\': ui.phoneList === \'waiting\', \'is-late\': longest >= 20 }"',
    '      :aria-expanded="ui.phoneList === \'waiting\' ? \'true\' : \'false\'"',
    '      @click="open(\'waiting\')">',
    '      Waiting <span class="pstrip__n">{{ waiting.length }}</span>',
    /* The longest wait, not the count, is what turns this from a queue into a
       problem — four people who arrived a minute ago and one person standing
       there half an hour are very different afternoons. */
    '      <span v-if="longest >= 5" class="pstrip__late">{{ longest }} min</span>',
    '      <span class="pstrip__caret" aria-hidden="true">{{ ui.phoneList === "waiting" ? "\\u2039" : "\\u203A" }}</span>',
    '    </button>',
    '  </div>',
    '',
    /* Expanded in place rather than in a sheet: these are short lists, and a
       sheet over the board would hide the columns the rows are about. */
    '  <div v-if="ui.phoneList" class="pstrips__open">',
    '    <due-now v-if="ui.phoneList === \'due\' && due.length" />',
    '    <waiting-list v-else-if="ui.phoneList === \'waiting\' && waiting.length" />',
    '  </div>',
    '</div>'
  ].join('\n')
};

/* ---------------- the bar itself ---------------- */

export const PhoneBar = {
  name: 'PhoneBar',
  setup: function () {
    function go(view) {
      store.closePhoneExtras();
      // The pane collapses to a 34px tab beside the board; as a whole screen
      // there is nothing for it to make room for, so it is opened outright
      // rather than landing on a tab somebody has to find and press again.
      if (view === 'book') store.appts.open = true;
      store.ui.view = view;
    }

    function openSheet() {
      // A shut book has nothing to start. The notice above the board says so
      // already, so this only has to not open onto a list of dead ends.
      if (store.refuseWhileClosed()) return;
      store.ui.phoneMore = false;
      store.ui.serviceSheet = true;
    }

    function toggleMore() {
      store.ui.serviceSheet = false;
      store.ui.phoneMore = !store.ui.phoneMore;
    }

    function pick(fn) {
      store.ui.phoneMore = false;
      fn();
    }

    function goSettings() { go('settings'); }

    // The board's shape, named for what pressing it would give you rather than
    // for what is on screen — a button labelled with the state you are already
    // in is the one everybody presses by mistake.
    var shapeLabel = Vue.computed(function () {
      return store.boardIsList.value ? 'Show the turn grid' : 'Show the list';
    });

    return {
      ui: store.ui,
      go: go,
      goSettings: goSettings,
      openSheet: openSheet,
      toggleMore: toggleMore,
      pick: pick,
      shapeLabel: shapeLabel,
      isList: store.boardIsList,
      toggleBoardMode: store.toggleBoardMode,
      scheduled: store.schedulingOn,
      armed: store.armedService,
      disarm: store.disarm,
      requestMode: Vue.computed(function () { return store.ui.requestMode; }),
      suggestedName: Vue.computed(function () {
        var id = store.suggestedStaffId.value;
        var s = id ? store.staffOf(id) : null;
        return s ? s.name : null;
      }),
      openMissed: store.openMissedWalkin,
      missedPeople: store.missedTodayPeople,
      openEndDay: store.openEndDay,
      closed: store.dayClosed
    };
  },
  template: [
    '<div class="pbar-dock">',
    /* What is armed, and the way out of it. Directly above the bar because it
       is a mode the whole screen is in — every row of the board is behaving
       differently while it is up — and a mode with no visible exit is how a
       phone ends up with a square on the wrong technician. */
    '  <div v-if="armed" class="parmed">',
    '    <span class="parmed__text">',
    '      <strong>{{ armed.name }}</strong>',
    '      <template v-if="requestMode"> request — tap the tech they asked for.</template>',
    '      <template v-else-if="suggestedName"> — next up is <strong>{{ suggestedName }}</strong>. Tap a row.</template>',
    '      <template v-else> — tap a row.</template>',
    '    </span>',
    '    <button type="button" class="parmed__x" @click="disarm">Cancel</button>',
    '  </div>',
    '',
    /* The overflow. Everything in it is either once a day or a change to how
       the board is drawn — nothing here is part of seating a client, which is
       the test for whether something earns a place on the bar itself. */
    '  <div v-if="ui.phoneMore" class="pmore">',
    '    <button type="button" class="pmore__item" @click="pick(toggleBoardMode)">',
    '      <span class="pmore__icon" aria-hidden="true">{{ isList ? "\\u25A6" : "\\u2261" }}</span>',
    '      {{ shapeLabel }}</button>',
    '    <button type="button" class="pmore__item" @click="pick(goSettings)">',
    '      <span class="pmore__icon" aria-hidden="true">&#9881;</span> Settings</button>',
    '    <button type="button" class="pmore__item" @click="pick(openMissed)">',
    '      <span class="pmore__icon" aria-hidden="true">&#8722;</span> Missed walk-in',
    '      <span v-if="missedPeople" class="pmore__n">{{ missedPeople }}</span></button>',
    '    <button type="button" class="pmore__item pmore__item--end" @click="pick(openEndDay)">',
    '      <span class="pmore__icon" aria-hidden="true">&#9632;</span>',
    '      {{ closed ? "Day closed" : "End day" }}</button>',
    '  </div>',
    '  <div v-if="ui.phoneMore" class="pmore__scrim" @click="toggleMore"></div>',
    '',
    '  <nav class="pbar" aria-label="Turn tracker">',
    '    <button type="button" class="pbar__btn" :class="{ \'is-on\': ui.view === \'board\' }"',
    '      @click="go(\'board\')">',
    '      <span class="pbar__icon" aria-hidden="true">&#9638;</span>Board</button>',
    '    <button v-if="scheduled" type="button" class="pbar__btn"',
    '      :class="{ \'is-on\': ui.view === \'book\' }" @click="go(\'book\')">',
    '      <span class="pbar__icon" aria-hidden="true">&#128197;</span>Book</button>',
    '    <button type="button" class="pbar__add" @click="openSheet"',
    '      :disabled="closed" aria-label="Start a turn"',
    '      :title="closed ? \'The book is closed for today\' : \'Start a turn\'">',
    '      <span aria-hidden="true">+</span></button>',
    '    <button type="button" class="pbar__btn" :class="{ \'is-on\': ui.view === \'history\' }"',
    '      @click="go(\'history\')">',
    '      <span class="pbar__icon" aria-hidden="true">&#8986;</span>History</button>',
    '    <button type="button" class="pbar__btn" :class="{ \'is-on\': ui.phoneMore }"',
    '      @click="toggleMore" aria-label="More">',
    '      <span class="pbar__icon" aria-hidden="true">&#8943;</span>More</button>',
    '  </nav>',
    '</div>'
  ].join('\n')
};
